import React from 'react';
import { Search, Star, RefreshCw, Inbox, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import type { MarketingEmailCampaign, MarketingEmailFolder } from '../../../models/MasterAdmin/marketingEmail';
import { AUDIENCE_SEGMENT_LABELS } from '../../../models/MasterAdmin/marketingEmail';
import {
  meBadgeClass,
  meGhostBtnClass,
  meIconBtnClass,
  meListPanelClass,
  meSecondaryBtnClass,
} from './marketingEmailUi';

interface EmailListPanelProps {
  emails: MarketingEmailCampaign[];
  selectedId: string | null;
  selectedIds: string[];
  activeFolder: MarketingEmailFolder;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  onSelect: (id: string) => void;
  onToggleSelect: (id: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onBulkDelete: () => void;
  onEmptyTrash?: () => void;
  onToggleStar: (id: string) => void;
  onRefresh: () => void;
  loading?: boolean;
  bulkDeleting?: boolean;
  page?: number;
  totalPages?: number;
  total?: number;
  onPageChange?: (page: number) => void;
}

function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const sameDay =
    d.getDate() === now.getDate() && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  if (sameDay) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (d.getFullYear() === now.getFullYear()) return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
}

function previewText(html: string): string {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > 60 ? `${text.slice(0, 60)}…` : text;
}

function recipientLabel(email: MarketingEmailCampaign): string {
  const recipients = email.sentRecipients?.length ? email.sentRecipients : email.to;
  if (recipients.length > 0) {
    if (recipients.length === 1) return recipients[0];
    if (recipients.length <= 3) return recipients.join(', ');
    return `${recipients.slice(0, 2).join(', ')} +${recipients.length - 2} more`;
  }
  if (email.recipientMode === 'custom') {
    return 'No recipients';
  }
  return AUDIENCE_SEGMENT_LABELS[email.audienceSegment];
}

const EmailListPanel: React.FC<EmailListPanelProps> = ({
  emails,
  selectedId,
  selectedIds,
  activeFolder,
  searchQuery,
  onSearchChange,
  onSelect,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
  onBulkDelete,
  onEmptyTrash,
  onToggleStar,
  onRefresh,
  loading = false,
  bulkDeleting = false,
  page = 1,
  totalPages = 1,
  total = 0,
  onPageChange,
}) => {
  const selectedSet = new Set(selectedIds);
  const allPageSelected = emails.length > 0 && emails.every((email) => selectedSet.has(email.id));
  const somePageSelected = emails.some((email) => selectedSet.has(email.id));
  const inTrash = activeFolder === 'trash';
  const deleteLabel = inTrash ? 'Delete permanently' : 'Move to trash';

  return (
    <div className={meListPanelClass}>
      <div className="border-b border-gray-200/80 px-3 py-3 dark:border-gray-800">
        <div className="flex items-center gap-2 rounded-xl border border-gray-200/80 bg-gray-50/80 px-3 py-2 dark:border-gray-700 dark:bg-gray-950/50">
          <Search className="h-4 w-4 shrink-0 text-gray-400" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search…"
            className="w-full border-0 bg-transparent text-sm outline-none placeholder:text-gray-400 dark:text-gray-100"
          />
          <button type="button" onClick={onRefresh} className={meIconBtnClass} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {emails.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 px-1">
            <label className="flex cursor-pointer items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <input
                type="checkbox"
                checked={allPageSelected}
                ref={(el) => {
                  if (el) el.indeterminate = somePageSelected && !allPageSelected;
                }}
                onChange={() => {
                  if (allPageSelected) onClearSelection();
                  else onSelectAll();
                }}
                className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary/30 dark:border-gray-600"
              />
              Select all
            </label>

            {selectedIds.length > 0 && (
              <>
                <span className="text-[11px] text-gray-400">{selectedIds.length} selected</span>
                <button
                  type="button"
                  onClick={onBulkDelete}
                  disabled={bulkDeleting}
                  className={`${meSecondaryBtnClass} !px-2 !py-1 !text-xs text-red-600 dark:text-red-400`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  {bulkDeleting ? 'Deleting…' : deleteLabel}
                </button>
                <button type="button" onClick={onClearSelection} className={`${meGhostBtnClass} !px-2 !py-1 !text-xs`}>
                  Clear
                </button>
              </>
            )}

            {inTrash && onEmptyTrash && total > 0 && (
              <button
                type="button"
                onClick={onEmptyTrash}
                disabled={bulkDeleting}
                className={`${meGhostBtnClass} ml-auto !px-2 !py-1 !text-xs text-red-600 dark:text-red-400`}
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete all ({total})
              </button>
            )}
          </div>
        )}

        <p className="mt-2 px-1 text-[11px] text-gray-400">
          {total > 0 ? `${total} campaign${total !== 1 ? 's' : ''}` : `${emails.length} campaign${emails.length !== 1 ? 's' : ''}`}
          {totalPages > 1 && ` · page ${page} of ${totalPages}`}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="space-y-2 p-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-xl bg-gray-100 p-3 dark:bg-gray-800" />
            ))}
          </div>
        ) : emails.length === 0 ? (
          <div className="flex flex-col items-center px-4 py-16 text-center">
            <Inbox className="mb-2 h-9 w-9 text-gray-300 dark:text-gray-600" />
            <p className="text-sm text-gray-500">No campaigns here</p>
          </div>
        ) : (
          emails.map((email) => {
            const active = selectedId === email.id;
            const checked = selectedSet.has(email.id);
            return (
              <div
                key={email.id}
                role="button"
                tabIndex={0}
                onClick={() => onSelect(email.id)}
                onKeyDown={(e) => e.key === 'Enter' && onSelect(email.id)}
                className={[
                  'mx-2 mb-1 cursor-pointer rounded-xl px-3 py-2.5 transition',
                  active
                    ? 'bg-primary/10 ring-1 ring-primary/20 dark:bg-primary/15'
                    : checked
                      ? 'bg-gray-100 dark:bg-gray-800/80'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-800/60',
                ].join(' ')}
              >
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onToggleSelect(email.id)}
                    className="mt-1 h-3.5 w-3.5 shrink-0 rounded border-gray-300 text-primary focus:ring-primary/30 dark:border-gray-600"
                    aria-label={`Select ${email.subject || 'campaign'}`}
                  />
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleStar(email.id); }}
                    className="mt-0.5 shrink-0"
                  >
                    <Star className={`h-3.5 w-3.5 ${email.starred ? 'fill-amber-400 text-amber-400' : 'text-gray-300 dark:text-gray-600'}`} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                        {recipientLabel(email)}
                      </span>
                      <span className="shrink-0 text-[10px] text-gray-400">
                        {formatDate(email.sentAt || email.scheduledAt || email.updatedAt)}
                      </span>
                    </div>
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {email.subject || '(no subject)'}
                    </p>
                    <div className="mt-1 flex items-center gap-2">
                      <span className={meBadgeClass(email.status)}>{email.status}</span>
                      <span className="truncate text-xs text-gray-400">{previewText(email.htmlBody)}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {totalPages > 1 && onPageChange && (
        <div className="flex shrink-0 items-center justify-between border-t border-gray-200/80 px-3 py-2 dark:border-gray-800">
          <button
            type="button"
            disabled={page <= 1 || loading}
            onClick={() => onPageChange(page - 1)}
            className={meGhostBtnClass + ' !px-2 !py-1.5 disabled:opacity-40'}
          >
            <ChevronLeft className="h-4 w-4" />
            Prev
          </button>
          <span className="text-xs text-gray-500">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages || loading}
            onClick={() => onPageChange(page + 1)}
            className={meGhostBtnClass + ' !px-2 !py-1.5 disabled:opacity-40'}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailListPanel;
